import React, {useContext, useEffect, useState} from 'react';
import * as ReactDOM from 'react-dom';
import GoogleMapReact from 'google-map-react';
import appContext from '../../Foundations/Contexts/AppContext';
import GoogleApiKey from '../../Foundations/Constants/GoogleApiKey';
import searchFilterContext from '../../Foundations/Contexts/SearchFilterContext';
import CircleMapMarker from './CircleMapMarker';
import MapViewSlider from './MapViewSlider';
import DepotMapMarker from './DepotMapMarker';
import OutOfBoundDepotMarker from './OutOfBoundDepotMarker';
import SvgIcon from '../../Components/Icons/SvgIcon';
import CloseIcon from '../../Components/Icons/CloseIcon';
import Button from '../../Components/Button';
import resultsContext from '../../Foundations/Contexts/ResultsContext';
import {useIsMounted} from '../../Foundations/Hooks/UseIsMounted';
import {getOutOfBoundsDepots} from '../../Foundations/Repositories/DepotRepository';
import {IDepotInfo} from '../../Foundations/Types/IDepot';

const MapView: React.FC = () => {
    const { env } = useContext(appContext);
    const { searchParameters } = useContext(searchFilterContext);
    const { setMapView, resultSummary, setSelectedRadius, selectedRadius } = useContext(resultsContext);
    const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
    const [circle, setCircle] = useState<google.maps.Circle | null >(null);
    const [outOfBoundDepots, setOutOfBoundDepots] = useState<IDepotInfo[]>([]);
    const isMounted = useIsMounted();

    // @Todo find a better way to do this
    useEffect(() => {
        const rootEl: HTMLElement | null = document.getElementById('root');

        if (rootEl !== null) {
           rootEl.style.width = '58.333333%';
           rootEl.style.padding = '0 15px';
        }

        if (searchParameters !== null && outOfBoundDepots.length === 0) {
            getOutOfBoundsDepots(env, searchParameters)
                .then((result) => {
                    if (isMounted() === true) {
                        setOutOfBoundDepots(result);
                    }
                });
        }

        return () => {
            if (rootEl !== null) { rootEl.style.width = ''; rootEl.style.padding = ''; }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
       if (circle !== null) {
           circle.setRadius(selectedRadius);
       }

       if (mapInstance !== null && circle !== null) {
           mapInstance.fitBounds(circle.getBounds(), 0);
           mapInstance.setZoom(mapInstance.getZoom());
       }

    }, [selectedRadius, circle, mapInstance]);

    const drawCircle = (map: google.maps.Map, radius: number) => {
        if (map === null) {
            return;
        }

        if (circle === null && searchParameters !== null) {
            const circle = new google.maps.Circle({
                strokeColor: '#0A68FF',
                strokeOpacity: 0.8,
                strokeWeight: 1,
                fillColor: '#0A68FF',
                fillOpacity: 0.1,
                map,
                center: {
                    lat: searchParameters.latitude,
                    lng: searchParameters.longitude
                },
                radius
            });
            setCircle(circle);
        } else if (circle !== null) {
            circle.setRadius(radius);
        }
    }

    const onMapLoaded = (map) => {
        const remove_poi = [
            {
                "featureType": "poi",
                "elementType": "labels",
                "stylers": [
                    { "visibility": "off" }
                ]
            }
        ];

        map.setOptions({styles: remove_poi});
        setMapInstance(map);

        // draw radius circle
        drawCircle(map, selectedRadius);
    }

    if (searchParameters === null) {
        return null;
    }
    return ReactDOM.createPortal(
        <div className="h-screen w-5/12 fixed top-0 right-0">
            <Button
                onClick={() => setMapView(false)}
                text="close"
                bg="bg-white"
                width="flex absolute z-20"
                height="left-1.5 top-1.5"
                rounded="rounded"
            >
                <SvgIcon
                    className="fill-gray-700"
                    viewBox="0 0 32 32"
                    width="32"
                    height="32"
                >
                    <CloseIcon/>
                </SvgIcon>
            </Button>
            <div style={{height: 'calc(100vh - 100px)', width: '100%'}}>
                <GoogleMapReact
                    bootstrapURLKeys={{
                        key: GoogleApiKey[env],
                        version: 'quarterly',
                    }}
                    yesIWantToUseGoogleMapApiInternals
                    onGoogleApiLoaded={({map}) => onMapLoaded(map)}
                    defaultCenter={{
                        lat: searchParameters.latitude,
                        lng: searchParameters.longitude
                    }}
                    defaultZoom={13}
                >
                    {
                        outOfBoundDepots.map((depot, index) => (
                            <OutOfBoundDepotMarker
                                lat={depot.coordinates.y}
                                lng={depot.coordinates.x}
                                key={index}
                                depot={depot}
                                circleRadius={selectedRadius}
                                depotSummary={resultSummary ? resultSummary.depotSummary : null}
                            />
                        ))
                    }
                    <CircleMapMarker
                        lat={searchParameters.latitude}
                        lng={searchParameters.longitude}
                        height={32}
                        width={32}
                        className="bg-orange z-20"
                    />
                    {
                        resultSummary !== null &&
                        resultSummary.depotSummary.map((marker, index) => (
                            <DepotMapMarker
                                key={index}
                                lat={marker.depot_coordinates.y}
                                lng={marker.depot_coordinates.x}
                                marker={marker}
                            />
                        ))
                    }
                </GoogleMapReact>
            </div>
            {
                mapInstance !== null &&
                <MapViewSlider
                    drawCircle={drawCircle}
                    mapInstance={mapInstance}
                    setCircleRadius={setSelectedRadius}
                />
            }
        </div>,
        document.body
    );
}

export default MapView;
